import type { IStation } from './interfaces'

export type StationFeedbackStatus = 'online' | 'offline' | 'no_feedback'

export function getStationFeedbackStatus(station: Pick<IStation, 'online' | 'feedback_status'>): StationFeedbackStatus {
  if (station.feedback_status === 'online' || station.feedback_status === 'offline' || station.feedback_status === 'no_feedback') {
    return station.feedback_status
  }
  return station.online ? 'online' : 'offline'
}

export function stationFeedbackSortRank(status: StationFeedbackStatus): number {
  if (status === 'online') return 0
  if (status === 'no_feedback') return 1
  return 2
}

/**
 * Same rule as Station page Diagnostics: non-zero top-level telemetry error_code.
 * Passive (no_feedback) and offline stations never surface diagnostics in the UI.
 */
export function stationHasDiagnosticsError(
  station: Pick<IStation, 'diagnostics_error_code' | 'online' | 'feedback_status'>
): boolean {
  const feedback = getStationFeedbackStatus(station)
  if (feedback === 'no_feedback' || feedback === 'offline') {
    return false
  }
  return Number(station.diagnostics_error_code ?? 0) !== 0
}

export function stationFeedbackLabel(status: StationFeedbackStatus): string {
  if (status === 'online') return 'Online'
  // Short badge / Connection value — not an outage; capability of the station type.
  if (status === 'no_feedback') return 'No live status'
  return 'Offline'
}

export function stationFeedbackTitle(status: StationFeedbackStatus): string | undefined {
  if (status === 'no_feedback') {
    return 'Without live status by design — this station type does not send telemetry'
  }
  if (status === 'offline') {
    return 'Station is offline — no recent MQTT telemetry'
  }
  return undefined
}

/** Short Assets Status badge for lot config vs last MQTT report. */
export const STATION_INFO_MISMATCH_BADGE = 'Info mismatch'

export function formatInventoryMismatchTitle(station: IStation): string | undefined {
  if (!station.inventory_mismatch || station.expects_telemetry === false) {
    return undefined
  }
  // Stale inventory from last MQTT is noise when the station is offline / passive.
  if (getStationFeedbackStatus(station) !== 'online') {
    return undefined
  }
  const onlyDb = station.lot_ids_only_in_db?.length
    ? `configured only: ${station.lot_ids_only_in_db.join(', ')}`
    : null
  const onlyTel = station.lot_ids_only_in_telemetry?.length
    ? `reported only: ${station.lot_ids_only_in_telemetry.join(', ')}`
    : null
  const modelParts = (station.lot_model_mismatches || []).map(
    item =>
      `lot ${item.lot_id}: DB ${item.configured_lot_model} vs telemetry ${item.telemetry_lot_model}`
  )
  const parts = [...[onlyDb, onlyTel].filter(Boolean), ...modelParts]
  return parts.length
    ? `Station info mismatch — ${parts.join('; ')}`
    : 'Station info mismatch — configured lots differ from last telemetry'
}
